import {
  action,
  atom,
  onConnect,
  onCtxAbort,
  reaction,
  readonly,
  sleep,
  withAssign,
} from '@reatom/framework'

import type { Order, Ordering } from '@blade/std-lib'
import type { Atom } from '@reatom/framework'

const FLASH_DURATION_MS = 800

type ReatomTemporalDistinctArgs<T> = {
  milliseconds?: number
  isEnabled?: Atom<boolean>
  order: Order.Order<T>
  name?: string
}

export const reatomTemporalDistinct = <T>(
  valueAtom: Atom<T>,
  options: ReatomTemporalDistinctArgs<T>,
) => {
  const { order, milliseconds = FLASH_DURATION_MS } = options
  const name = options.name ?? 'temporalDistinct'

  const temporalDistinctAtom = atom<Ordering.Ordering | undefined>(
    undefined,
    `${name}.temporalDistinctAtom`,
  )
  const prevValueAtom = atom<T | undefined>(undefined, `${name}.prevValueAtom`)
  const flashPulseStateAtom = atom(0, `${name}.flashPulseStateAtom`)

  const resetTemporalDistinctAction = action((ctx) => {
    temporalDistinctAtom(ctx, undefined)
  }, `${name}.resetTemporalDistinctAction`)

  const distinctReaction = reaction(async (ctx) => {
    const currentValue = ctx.spy(valueAtom)
    const prevValue = ctx.get(prevValueAtom)

    // Инициализация: запоминаем стартовое значение без визуальной реакции
    if (prevValue === undefined) {
      prevValueAtom(ctx, currentValue)
      return
    }

    const compareResult = order(currentValue, prevValue)
    if (compareResult === 0) {
      return
    }

    // Фикс #1: проверяем isEnabled ДО обновления prevValue.
    // Если отключено — молча обновляем базу сравнения и выходим,
    // иначе следующее изменение сравнивалось бы с "невидимым" значением.
    if (options.isEnabled && !ctx.get(options.isEnabled)) {
      prevValueAtom(ctx, currentValue)
      return
    }

    prevValueAtom(ctx, currentValue)

    // Фиксируем версию пульса до await, чтобы после сна
    // убедиться, что за это время не пришло новое изменение.
    flashPulseStateAtom(ctx, (state) => state + 1)
    const versionOnStart = ctx.get(flashPulseStateAtom)

    temporalDistinctAtom(ctx, compareResult)

    // onCtxAbort срабатывает при отписке всего контекста реакции —
    // это корректное место для сброса при размонтировании компонента.
    onCtxAbort(ctx, () => {
      resetTemporalDistinctAction(ctx)
    })

    await ctx.schedule(() => sleep(milliseconds))

    // Фикс #2: сбрасываем только если за время sleep не пришла
    // новая итерация (она увеличила бы flashPulseState).
    if (ctx.get(flashPulseStateAtom) === versionOnStart) {
      resetTemporalDistinctAction(ctx)
    }
  }, `${name}.distinctReaction`)

  // TODO: connect two onConnect into single one
  onConnect(flashPulseStateAtom, (ctx) => {
    const unsub = distinctReaction(ctx)
    return () => {
      unsub.unsubscribe()
    }
  })

  const flashPulseAtom = atom((ctx) => {
    return ctx.spy(flashPulseStateAtom)
  }, `${name}.flashPulseAtom`)

  onConnect(temporalDistinctAtom, (ctx) => {
    const unsub = distinctReaction(ctx)
    return () => {
      unsub.unsubscribe()
    }
  })
  const isDistinctAtom = atom((ctx) => {
    const distinct = ctx.spy(temporalDistinctAtom)
    // Фикс #3: явная проверка вместо хрупкого !== 0
    return distinct !== undefined && distinct !== 0
  }, `${name}.isDistinctAtom`)

  return temporalDistinctAtom.pipe(
    readonly,
    withAssign(() => ({
      flashPulseAtom,
      isDistinctAtom,
      duration: milliseconds,
      value: valueAtom,
    })),
  )
}
